import { useEffect, useRef } from "react";
import { useAtom, useAtomValue } from "jotai";
import { RESET } from "jotai/utils";
import classNames from "classnames";
import { Icon } from "@common/index";
import { locationsLoadedAtom } from "@/store/locationAtoms";
import {
    activeShipToLocationAtom,
    browsesUnfilteredAtom,
    eligibleShipToLocationsAtom,
    hasNoLocationMembershipAtom,
    isShipToSwitcherOpenAtom,
    shipToLocationsStatusAtom,
    storedShipToLocationIdAtom,
} from "@/store/shipToAtoms";
import { NO_LOCATION_NOTE } from "@customer/_helpers/noLocationCopy";
import { SupportLinkedText } from "@customer/_components/SupportLinkedText";
import { useShipToLocations } from "../_helpers/useShipToLocations";

interface ShipToSwitcherProps {
    /** Mobile header sizing — the desktop cluster uses the default size. */
    compact?: boolean;
}

// Marks every switcher root. CustomerNavigation mounts DesktopNav AND MobileNav at once and
// hides one with a media query, so two switchers are always live against one global open atom,
// and "outside" has to mean outside EVERY one of them — see handlePointerDown.
const SWITCHER_ROOT_ATTRIBUTE = "data-ship-to-switcher";

// "Shipping to" — the destination every storefront read is answered against (#228 / E4-S12).
//
// Trigger follows the Account Dashboard mockup: truck glyph, the STATE abbreviation, chevron.
// The full location name is the accessible name and the label in the list, because "CA" alone
// does not identify which of two Californian locations is selected.
//
// Desktop placement is an INFERENCE, not a mockup: only the mobile lockup has been delivered
// (the desktop PNG is SharePoint-only), so the same lockup is mirrored into the desktop action
// cluster next to Search and Cart. Deliberately conservative and easy to correct.
export const ShipToSwitcher = ({ compact = false }: ShipToSwitcherProps) => {
    const status = useAtomValue(shipToLocationsStatusAtom);
    const eligibleLocations = useAtomValue(eligibleShipToLocationsAtom);
    const activeLocation = useAtomValue(activeShipToLocationAtom);
    const [isOpen, setIsOpen] = useAtom(isShipToSwitcherOpenAtom);
    const [storedLocationId, setStoredLocationId] = useAtom(
        storedShipToLocationIdAtom,
    );
    const locationsLoaded = useAtomValue(locationsLoadedAtom);
    // #417: an organisation member seated on no location. GET /locations is
    // filtered on membership, so their list loads EMPTY — but "no approved
    // location yet" would be the wrong explanation: the org's locations may
    // all be approved; this member just is not on any of them. Keyed on the
    // status the API reports for exactly that case, so a genuinely
    // location-less org (awaiting approval) keeps the generic copy.
    const hasNoLocationMembership = useAtomValue(hasNoLocationMembershipAtom);
    const browsesUnfiltered = useAtomValue(browsesUnfilteredAtom);
    const { retry } = useShipToLocations();

    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    // A stored id the LOADED list does not carry as eligible (the location was rejected,
    // suspended or removed since it was picked, or its organisation lapsed) is cleared here,
    // once, rather than left to fail on every load: shipToLocationIdAtom already stops sending
    // it, and activeShipToLocationAtom already labels the API's default, so this only stops the
    // same dead preference from being re-tried on the next hard load. The switcher is on every
    // customer page, which is why the clear lives here and not in the location hook (admin
    // surfaces read locations too).
    useEffect(() => {
        if (!locationsLoaded || !storedLocationId) return;
        if (eligibleLocations.some((l) => l.id === storedLocationId)) return;
        setStoredLocationId(RESET);
    }, [locationsLoaded, storedLocationId, eligibleLocations, setStoredLocationId]);

    // Escape and an outside click both close, and Escape returns focus to the trigger the way
    // the app's other overlays do. Only bound while open, so a closed switcher costs nothing.
    useEffect(() => {
        if (!isOpen) return;

        // The header is not sticky. When another surface opens this panel — the
        // restricted-lines notice at the bottom of a long cart, the not-available
        // product view — the trigger must be brought on screen, or the click
        // appears to do nothing. `nearest`, so an already-visible header does not
        // jump. Optional-called: jsdom has no scrollIntoView.
        containerRef.current?.scrollIntoView?.({ block: "nearest" });

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            // Every mounted switcher handles this Escape, but only the one that
            // actually holds focus may restore it: the twin's trigger is
            // display:none, so focusing it would drop focus to <body>.
            const ownsFocus = containerRef.current?.contains(
                document.activeElement,
            );
            setIsOpen(false);
            if (ownsFocus) triggerRef.current?.focus();
        };

        const handlePointerDown = (event: MouseEvent) => {
            // Outside EVERY switcher, not just this one. A press inside the visible
            // panel is outside the CSS-hidden twin's own container, so a containerRef
            // test closes the panel from the twin: it unmounts between mousedown and
            // mouseup, no click is dispatched, and the location can never be picked.
            // The same press on the trigger closed it and let onClick re-open it,
            // leaving the panel undismissable from the control that opened it.
            const target = event.target as Element | null;
            if (target?.closest?.(`[${SWITCHER_ROOT_ATTRIBUTE}]`)) return;
            setIsOpen(false);
        };

        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("mousedown", handlePointerDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("mousedown", handlePointerDown);
        };
    }, [isOpen, setIsOpen]);

    // #404: an admin's reads send no id and are answered unfiltered. There is no destination to
    // name, "no approved location yet" would be false, and a pick would change nothing an admin
    // reads, so there is no switcher. After every hook, so the hook order never changes.
    if (browsesUnfiltered) return null;

    const isLoading = status === "idle" || status === "loading";
    const hasFailed = status === "failed";

    // Four distinct states on the trigger, and `failed` is not allowed to look like any of the
    // others: a broken read renders an error glyph in error colour, never the truck-and-dash
    // that means "loaded, but you have no approved location yet".
    const triggerLabel = isLoading
        ? "Loading your shipping locations"
        : hasFailed
            ? "Shipping location unavailable — we couldn't load your locations"
            : activeLocation
                ? `Shipping to: ${activeLocation.name}`
                : hasNoLocationMembership
                    ? "Shipping to: no location assigned yet"
                    : "Shipping to: no approved location yet";

    const handleSelect = (locationId: string) => {
        setStoredLocationId(locationId);
        setIsOpen(false);
    };

    return (
        <div
            ref={containerRef}
            className="relative"
            data-testid="ship-to-switcher"
            {...{ [SWITCHER_ROOT_ATTRIBUTE]: "" }}
        >
            <button
                ref={triggerRef}
                type="button"
                aria-haspopup="true"
                aria-expanded={isOpen}
                aria-label={triggerLabel}
                disabled={isLoading}
                onClick={() => setIsOpen(!isOpen)}
                className={classNames(
                    "btn btn-ghost normal-case gap-1",
                    compact ? "btn-sm px-2" : "text-base font-bold",
                    hasFailed && "text-error",
                )}
            >
                {isLoading ? (
                    <span
                        data-testid="ship-to-loading"
                        className="loading loading-spinner loading-xs"
                    />
                ) : (
                    <>
                        <Icon
                            name={hasFailed ? "error" : "local_shipping"}
                            filled
                            className="text-xl"
                        />
                        <span data-testid="ship-to-state">
                            {activeLocation?.state ?? "—"}
                        </span>
                        <Icon
                            name="expand_more"
                            className="text-base text-accent"
                        />
                    </>
                )}
            </button>

            {isOpen && (
                <div
                    data-testid="ship-to-panel"
                    className="absolute right-0 z-20 mt-2 w-64 rounded-box bg-base-100 p-2 text-neutral shadow"
                >
                    {hasFailed ? (
                        <div role="alert" className="p-2 text-sm">
                            <p>We couldn&apos;t load your locations.</p>
                            <button
                                type="button"
                                onClick={retry}
                                className="btn btn-sm btn-primary mt-2"
                            >
                                Retry
                            </button>
                        </div>
                    ) : isLoading ? (
                        // In-flight is NOT loaded-but-empty. `disabled` keeps the trigger
                        // itself from opening mid-read, but the restricted-lines notice and
                        // the not-available product view open this panel through the atom
                        // regardless — so a shopper told to change location would be told,
                        // in the same breath, that they have none.
                        <p
                            className="p-2 text-sm text-neutral/70"
                            data-testid="ship-to-panel-loading"
                        >
                            Loading your shipping locations…
                        </p>
                    ) : eligibleLocations.length === 0 ? (
                        <p
                            className="p-2 text-sm text-neutral/70"
                            data-testid="ship-to-panel-empty"
                        >
                            {/* The open atom outlives the route and /support
                                mounts this same header, so following the
                                support link closes the panel (#453). */}
                            {hasNoLocationMembership ? (
                                <SupportLinkedText
                                    text={NO_LOCATION_NOTE}
                                    onLinkClick={() => setIsOpen(false)}
                                />
                            ) : (
                                "No approved locations yet."
                            )}
                        </p>
                    ) : (
                        <ul className="menu w-full p-0">
                            {eligibleLocations.map((location) => (
                                <li key={location.id}>
                                    <button
                                        type="button"
                                        aria-current={
                                            location.id === activeLocation?.id
                                        }
                                        onClick={() =>
                                            handleSelect(location.id)
                                        }
                                        className="flex flex-col items-start gap-0 text-left"
                                    >
                                        <span className="font-semibold">
                                            {location.name}
                                        </span>
                                        <span className="text-xs text-neutral/70">
                                            {location.city}, {location.state}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};
